// DCG V3.1.1 · 20 OCT 2026 · hotfix.1, H3 (docs/REVIEW-engineering-V3-1-0.md): a refused add is said. On a 422 ("Sold out", "Only N available"), a 429, any other refusal or no answer ("Couldn't add — try again"), productAddToCart shows the enforcer's toast, then puts the button back; it wrote only console.error. A refusal the enforcer has said already is not said twice. An answer that is not JSON (a 429's page) is read as text.
// DCG native cart bridge (piggybacks theme cart dropdown refresh)
window.DCG = window.DCG || {};
window.DCG.cart = window.DCG.cart || {};

window.DCG.cart.addItem = async function ({ id, quantity = 1, properties }) {
  // ── Pre-flight enforcement ─────────────────────────────────────────────
  // dcg-cart-enforcer.js (loaded before this script) checks limit:N tags
  // and cross-references the current cart to prevent over-limit adds.
  // If preflight is unavailable (edge case), we fall through silently.
  if (window.DCG && typeof window.DCG.enforcer === "object" && window.DCG.enforcer) {
    let _enforcerBlocked = false;
    try {
      const check = await window.DCG.enforcer.preflight(id, quantity);
      if (check.blocked) {
        // Mark as a deliberate block before throwing so the catch below
        // knows to re-throw rather than silently swallow the error.
        _enforcerBlocked = true;
        window.DCG.enforcer.showToast(check.message || "Purchase limit reached.");
        const blocked = new Error(check.message || "Purchase limit reached.");
        blocked.dcgSaid = true;           // V3.1.1 (H3): said by the toast above; productAddToCart says nothing more
        throw blocked;
      }
      if (check.message) {
        // Qty was reduced — show reason, but still proceed with the add
        window.DCG.enforcer.showToast(check.message);
      }
      // Use the (possibly adjusted) quantity going forward
      quantity = check.qty;
    } catch (enforceErr) {
      if (_enforcerBlocked) {
        // Deliberate block — re-throw so productAddToCart's .catch handler
        // cleans up the .adding button state without showing a success flash.
        throw enforceErr;
      }
      // Otherwise (network failure inside preflight) log and allow the add
      console.warn("[DCG] enforcer preflight error — allowing add:", enforceErr);
    }
  }

  const payload = { id: Number(id), quantity: Number(quantity || 1) };
  if (properties) payload.properties = properties;

  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    // Shopify returns JSON error with "description". V3.1.1 (H3): read from a copy, so that an answer that is not JSON
    // (a 429's page) is still read as text (the body was used up by the failed json() before); the status goes with the
    // error, for productAddToCart to say why.
    let msg = "Add to cart failed";
    let data = null;
    try { data = await res.clone().json(); } catch (_) {}
    if (data) msg = data?.description || data?.message || msg;
    else { try { msg = (await res.text()).slice(0, 200) || msg; } catch (_) {} }
    const refused = new Error(msg);
    refused.status = res.status;
    throw refused;
  }

  const data = await res.json();

  // THEME-NATIVE refresh hook:
  if (typeof window.adjustCartDropDown === "function") {
    window.adjustCartDropDown();
  } else {
    // fallback: at least trigger common theme events
    document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  }

  // Always dispatch cart:updated so dcg-cart-enforcer.js can run its
  // drawer audit regardless of which refresh path was taken above.
  document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));

  return data;
};

// ─────────────────────────────────────────────────────────────────────────────
// productAddToCart — global shim for shop.js
//
// shop.js (loaded on every page) calls productAddToCart(variantID, qty) inside
// its .nm-addToCart.enable click handler. That function is normally defined in
// quickview.js, but quickview.js is NOT loaded on the collection page.
// This shim fills that gap so the native ATC pathway works on collections
// without pulling in all of quickview.js.
//
// Guard: only define it if quickview.js hasn't already done so, so other pages
// (product, quick-view) continue to use their existing implementation.
// ─────────────────────────────────────────────────────────────────────────────
// CHECK_SVG is set by dcg-product-card.js on window.DCG before this runs.
// Fallback inline in case load order is unexpected.
var _CHECK_SVG = '<svg class="dcg-atc-icon" viewBox="0 0 20 20" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 10 8.5 15 16 6"/></svg>';

// V3.1.1 (hotfix.1, H3): what a customer is told when an add is not taken. 422 is Shopify's "not that many": sold out
// since the page was drawn ("The product '...' is already sold out.") or more than it has ("You can only add 3 ...",
// "All 3 ... are in your cart."). Any other refusal (429, too many requests; a 5xx) or no answer at all is a "try again".
function _dcgRefusedWords(err) {
  var status = err && err.status, text = String((err && err.message) || "");
  if (status === 422) {
    if (/sold out/i.test(text)) return "Sold out";
    var n = text.match(/\d+/);
    if (n) return "Only " + n[0] + " available";
  }
  return "Couldn't add — try again";
}
window.DCG.cart.refusedWords = _dcgRefusedWords;   // for the tests

if (typeof window.productAddToCart !== "function") {
  window.productAddToCart = function (varID, qty) {
    // Snapshot which buttons are in .adding state at the moment of the call.
    // shop.js adds .adding synchronously before calling us, so this is the
    // exact set of buttons the user just clicked.
    var pendingBtns = Array.from(
      document.querySelectorAll(".nm-addToCart.adding")
    );

    window.DCG.cart
      .addItem({ id: varID, quantity: qty })
      .then(function () {
        var checkSvg = (window.DCG && window.DCG.CHECK_SVG) || _CHECK_SVG;

        pendingBtns.forEach(function (btn) {
          btn.classList.remove("adding");

          // Store the original button HTML (set by dcg-product-card.js as a data-attr)
          // so we can restore it after the success flash.
          var origHtml = btn.dataset.dcgOrigHtml || btn.innerHTML;

          // Swap to checkmark. On desktop: icon only (label hidden by CSS).
          // On mobile: icon + "Added!" text label.
          btn.innerHTML = checkSvg + '<span class="dcg-atc-label">Added!</span>';
          btn.classList.add("dcg-atc-success");

          setTimeout(function () {
            btn.classList.remove("dcg-atc-success");
            btn.innerHTML = origHtml;
          }, 1400);
        });
      })
      .catch(function (err) {
        console.error("[DCG] Add to cart error:", err && err.message);
        // V3.1.1 (H3): the refusal is said (the enforcer's toast), unless the enforcer has said why already
        if (!(err && err.dcgSaid)) {
          var enf = window.DCG && window.DCG.enforcer;
          if (enf && typeof enf.showToast === "function") { try { enf.showToast(_dcgRefusedWords(err)); } catch (_) {} }
        }
        // then .adding is cleaned up — no success flash
        pendingBtns.forEach(function (btn) {
          btn.classList.remove("adding");
        });
      });
  };
}